import { gql } from "gql";
import {
  MeristemExperimentInfo,
  RecommendedBuyflowOffersInput,
  TrackingParams,
} from "gql/graphql";
import { getSurveyAnswers, useSurveyAnswers } from "src/hooks/survey/answers";
import { getURLParams } from "src/utils/urlParams";
import { getVisitedExperiments } from "src/utils/experiment";
import visitTracker from "@noom/visit-tracker";
import { apolloClient } from "./apolloClient";
import getStore from "src/utils/redux/store";
import { MED_SKU } from "src/utils/medEligibility";
import { captureException } from "src/utils/error";
import { trackEvent } from "src/utils/api/tracker";

export const RECOMMENDED_BUYFLOW_OFFERS = gql(`
  query RecommendedBuyflowOffers($input: RecommendedBuyflowOffersInput!) {
    recommendedBuyflowOffers(input: $input) {
      offers
      recommendationId
    }
  }
`);

function getTrackingParams(): TrackingParams {
  const urlParams = getURLParams();
  const visitTrackerParams = visitTracker.getTrackingParams();

  return {
    initialUrl: visitTrackerParams?.initialUrl,
    referrer: visitTrackerParams?.referrer,
    utmSource: urlParams.utm_source,
    utmMedium: urlParams.utm_medium,
    utmCampaign: urlParams.utm_campaign,
    utmContent: urlParams.utm_content,
    utmTerm: urlParams.utm_term,
    feature: urlParams.feature ? [urlParams.feature].flat() : [],
  };
}

function getExperimentsInfo(): MeristemExperimentInfo[] {
  const visitedExperiments = getVisitedExperiments();
  return visitedExperiments.map(([context, experiment]) => ({
    context,
    experimentName: experiment.experimentName,
    variationName: experiment.variationName,
  }));
}

function getFormattedSurveyAnswers(
  surveyAnswers: ReturnType<typeof useSurveyAnswers>
) {
  return Object.entries(surveyAnswers)
    .filter(([, value]) => value != null)
    .map(([questionId, answer]) => ({
      questionId,
      answerJson: JSON.stringify(answer),
    }));
}

export type RecommendedOffersResult = {
  recommendedMedTier?: MED_SKU;
  eligibleMedTiers: MED_SKU[];
  recommendationId?: string;
};

export async function queryRecommendedBuyflowOffers(
  eligibleSKUs: Array<MED_SKU>
): Promise<RecommendedOffersResult | null> {
  if (!eligibleSKUs.length) {
    return null;
  }
  let recommendationId: string | undefined;
  try {
    const state = getStore().getState();
    const { userData } = state;
    const surveyAnswers = getSurveyAnswers(state);

    const trackingParams = getTrackingParams();
    const experiments = getExperimentsInfo();
    const formattedSurveyAnswers = getFormattedSurveyAnswers(surveyAnswers);

    const input: RecommendedBuyflowOffersInput = {
      recommendationType: "MED_PLAN_OFFER",
      userId: userData.user_id,
      surveyAnswers: formattedSurveyAnswers,
      trackingParams,
      experiments,
      eligibleSKUsForRecommendation: eligibleSKUs,
    };

    const result = await apolloClient.query({
      query: RECOMMENDED_BUYFLOW_OFFERS,
      variables: {
        input,
      },
    });

    if (!result.data?.recommendedBuyflowOffers) {
      throw new Error(`No recommended buyflow offers for ${userData.user_id}`);
    }

    recommendationId = result.data.recommendedBuyflowOffers.recommendationId;
    const { offers } = result.data.recommendedBuyflowOffers;
    const mappedSKUs = mapOffersToMedSKUs(offers);

    if (mappedSKUs.length !== eligibleSKUs.length) {
      const mappedSKUsString = mappedSKUs.join(", ");
      const eligibleSKUsString = eligibleSKUs.join(", ");
      throw new Error(
        `Recommended buyflow offers do not match eligible SKUs: ${mappedSKUsString} !== ${eligibleSKUsString}`
      );
    }

    return {
      recommendedMedTier: mappedSKUs[0], // First entry is the recommendation
      eligibleMedTiers: mappedSKUs,
      recommendationId,
    };
  } catch (error) {
    trackEvent("OnRecommendedBuyflowOffersError", {
      error: error.message,
      eligibleSKUs,
      recommendationId,
    });
    captureException(
      error,
      "error-query-recommended-buyflow-offers",
      recommendationId
    );
    return null;
  }
}

/**
 * Maps GraphQL offer strings to MED_SKU values
 */
function mapOffersToMedSKUs(offers: string[]): MED_SKU[] {
  return offers
    .map((offer) => offer as MED_SKU)
    .filter((sku): sku is MED_SKU => Object.values(MED_SKU).includes(sku));
}
